import React, { useCallback, useState, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import {
  Button,
  Card,
  CardHeader,
  mergeClasses,
  Image,
} from '@fluentui/react-components';
import { ArrowLeft16Regular, Image16Regular, Checkmark20Regular, Dismiss20Regular, ArrowClockwise20Regular } from '@fluentui/react-icons';

import { AdPreviewSinglePlacementLayout } from '@bingads-webui-campaign-react/unified-ad-preview-library-cmui';
import { ImageCropWithBoundingBox } from '@bingads-webui-campaign-react/image-crop-with-bounding-box';

import { ImagePreviewView } from '../image-preview-view';
import { useStyles, usePlacementEditStyles } from './style';

export const PlacementEditView = ({
  placement,
  aspectRatioUIString,
  previewTypeString,
  adType,
  maxCropBox,
  previewImage,
  previewTexts,
  onSave,
  onCancel,
  i18n,
  logger,
  goToPlacementListView,
  updateCropSetting,
  permissions,
}) => {
  const {
    placementMeta,
    placementCropping, // for single image placement
    images, // for multi-image placement
  } = placement || {};

  const [curPlacementCropping, setCurPlacementCropping]
    = useState(() => placementCropping || (images && images[0] && images[0].placementCropping));

  const handleCropSettingsChanged = useCallback((settings) => {
    if (!_.isEqual(settings, curPlacementCropping)) {
      setCurPlacementCropping(settings);
      logger.logTrace('edit placement: crop settings changed', {
        adType,
        placementType: placementMeta && placementMeta.imageType,
        cropSettings: JSON.stringify(settings),
      });
    }
  }, [adType, curPlacementCropping, logger, placementMeta]);

  const handleCancel = useCallback(() => {
    onCancel();
    goToPlacementListView();
  }, [goToPlacementListView, onCancel]);

  const handleSave = useCallback(() => {
    onSave(placement, curPlacementCropping);
    if (updateCropSetting) {
      updateCropSetting({ cropping: curPlacementCropping, imageType: placement.placementMeta.imageType });
    }
    goToPlacementListView();
  }, [curPlacementCropping, goToPlacementListView, onSave, placement, updateCropSetting]);

  const onRenderPlacement = useCallback((p, AdPlacementComponent) => {
    if (!p || !AdPlacementComponent) {
      return null;
    }
    // override the cropping
    let newPlacement = {
      ...p, placementCropping: curPlacementCropping, expandedPlacementCropping: undefined, effectiveImageUrl: undefined, effectiveImageWidth: undefined, effectiveImageHeight: undefined,
    };
    if (!_.isEmpty(p.images)) {
      // for multi-image placement, override the placementCropping of the first image
      const newFirstImg = { ...p.images[0], placementCropping: curPlacementCropping };
      newPlacement = { ...p, images: [newFirstImg, ...p.images.slice(1)] };
    }

    return (
      <AdPlacementComponent
        placementData={newPlacement}
        {...previewTexts}
        i18n={i18n}
      />);
  }, [curPlacementCropping, i18n, previewTexts]);

  const imageCropView = useMemo(() => (
    <ImageCropWithBoundingBox
      baseImage={previewImage}
      maxWidth={598}
      maxHeight={1000}
      minCropSize={placementMeta.minSize}
      cropSettings={curPlacementCropping}
      aspectRatio={placementMeta.aspectRatio}
      onCropSettingsChanged={handleCropSettingsChanged}
      maxCropBox={maxCropBox}
    />
  ), [curPlacementCropping, handleCropSettingsChanged, maxCropBox, placementMeta.aspectRatio, placementMeta.minSize, previewImage]);

  const buttonsConfig = useMemo(() => ([{
    onClick: handleSave,
    icon: Checkmark20Regular,
    text: i18n.getString(_TL_('Apply')),
  }, {
    onClick: handleCancel,
    icon: Dismiss20Regular,
    text: i18n.getString(_TL_('Cancel')),
  }]), [handleCancel, handleSave, i18n]);

  const placementEditView = useStyles();

  return (
    <div className={mergeClasses(placementEditView, 'placement-edit-view')}>
      <Card className="card">
        <CardHeader
          header={
            <Button
              appearance="subtle"
              icon={<ArrowLeft16Regular />}
              onClick={() => { goToPlacementListView(); }}
              className="button"
            >
              {i18n.getString(_TL_('Edit aspect ratio'))}
            </Button>
          }
        />
        <div className="ad-preview-wrapper">
          <div className="placement-preview-text-wrapper">
            <div className="placement-preview-text">
              <Image16Regular />
              {i18n.getString(_TL_('{{ratio}} {{previewType}}'), { ratio: aspectRatioUIString, previewType: i18n.getString(previewTypeString) })}
            </div>
          </div>
          <AdPreviewSinglePlacementLayout
            placementData={placement}
            onRenderPlacement={onRenderPlacement}
          />
        </div>
      </Card>
      <ImagePreviewView
        imageView={imageCropView}
        buttonsConfig={buttonsConfig}
        permissions={permissions}
      />
    </div>
  );
};

PlacementEditView.propTypes = {
  placement: PropTypes.shape({}),
  aspectRatioUIString: PropTypes.string.isRequired,
  previewTypeString: PropTypes.string.isRequired,
  adType: PropTypes.string.isRequired,
  maxCropBox: PropTypes.shape({}),
  previewImage: PropTypes.shape({
    Type: PropTypes.string,
    Src: PropTypes.string,
    Width: PropTypes.number,
    Height: PropTypes.number,
  }).isRequired,
  previewTexts: PropTypes.shape({}).isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
  }).isRequired,
  goToPlacementListView: PropTypes.func.isRequired,
};

PlacementEditView.defaultProps = {
  placement: null,
  maxCropBox: null,
};

export const PlacementEditViewV2 = ({
  placement,
  aspectRatioUIString,
  previewTypeString,
  adType,
  maxCropBox,
  previewImage,
  previewTexts,
  onSave,
  onCancel,
  i18n,
  logger,
  goToPlacementListView,
  onToggleAspectRatio,
  canExcludeAspectRatio,
  includedTypeList,
  permissions,
}) => {
  const {
    placementMeta,
    placementCropping, // for single image placement
    images, // for multi-image placement
  } = placement || {};

  const [cropViewRefreshKey, setCropViewRefreshKey] = React.useState(0);
  const [curPlacementCropping, setCurPlacementCropping]
    = useState(() => placementCropping || (images && images[0] && images[0].placementCropping));
  const initialPlacementCropping = useRef(curPlacementCropping);

  const handleCropSettingsChanged = useCallback((settings) => {
    if (!_.isEqual(settings, curPlacementCropping)) {
      setCurPlacementCropping(settings);
      logger.logTrace('edit placement: crop settings changed', {
        adType,
        placementType: placementMeta && placementMeta.imageType,
        cropSettings: JSON.stringify(settings),
      });
    }
  }, [adType, curPlacementCropping, logger, placementMeta]);

  const handleCancel = useCallback(() => {
    logger.logTrace('edit placement: cancel button clicked');
    onCancel();
    goToPlacementListView();
  }, [goToPlacementListView, onCancel, logger]);

  const handleSave = useCallback(() => {
    logger.logTrace('edit placement: save button clicked');
    onSave(placement, curPlacementCropping);
    goToPlacementListView();
  }, [curPlacementCropping, goToPlacementListView, onSave, placement, logger]);

  const handleReset = useCallback(() => {
    handleCropSettingsChanged(initialPlacementCropping.current);
    logger.logTrace('edit placement: reset button clicked');
    setCropViewRefreshKey(prev => prev + 1);
  }, [handleCropSettingsChanged, logger]);

  const onRenderPlacement = useCallback((p, AdPlacementComponent) => {
    if (!p || !AdPlacementComponent) {
      return null;
    }
    // override the cropping
    let newPlacement = {
      ...p, placementCropping: curPlacementCropping, expandedPlacementCropping: undefined, effectiveImageUrl: undefined, effectiveImageWidth: undefined, effectiveImageHeight: undefined,
    };
    if (!_.isEmpty(p.images)) {
      // for multi-image placement, override the placementCropping of the first image
      const newFirstImg = { ...p.images[0], placementCropping: curPlacementCropping };
      newPlacement = { ...p, images: [newFirstImg, ...p.images.slice(1)] };
    }

    return (
      <AdPlacementComponent
        placementData={newPlacement}
        {...previewTexts}
        i18n={i18n}
      />);
  }, [curPlacementCropping, i18n, previewTexts]);

  const renderIncludeAndExcludeButton = useCallback(() => {
    if (!includedTypeList || !_.isArray(includedTypeList) || !canExcludeAspectRatio || !onToggleAspectRatio || !placementMeta) {
      return null;
    }

    const canBeExcluded = canExcludeAspectRatio(placementMeta.imageType);

    if (includedTypeList.includes(placementMeta.imageType)) {
      return <Button className={mergeClasses('action-button', 'exclude-button')} disabled={!canBeExcluded} onClick={() => onToggleAspectRatio(placementMeta.imageType, false)}> {i18n.getString(_TL_('Exclude'))}</Button>;
    }

    return <Button className={mergeClasses('action-button', 'include-button')} onClick={() => onToggleAspectRatio(placementMeta.imageType, true)}> {i18n.getString(_TL_('Include'))}</Button>;
  }, [canExcludeAspectRatio, i18n, includedTypeList, onToggleAspectRatio, placementMeta]);

  const imageCropView = useMemo(() => (
    <ImageCropWithBoundingBox
      key={cropViewRefreshKey}
      baseImage={previewImage}
      maxWidth={308}
      maxHeight={1000}
      minCropSize={placementMeta.minSize}
      cropSettings={curPlacementCropping}
      aspectRatio={placementMeta.aspectRatio}
      onCropSettingsChanged={handleCropSettingsChanged}
      maxCropBox={maxCropBox}
    />
  ), [curPlacementCropping, handleCropSettingsChanged, maxCropBox, placementMeta.aspectRatio, placementMeta.minSize, previewImage, cropViewRefreshKey]);

  const placementPreviewView = useMemo(() => (
    <div className="placement-preview-wrapper">
      <AdPreviewSinglePlacementLayout
        placementData={placement}
        onRenderPlacement={onRenderPlacement}
      />
    </div>
  ), [onRenderPlacement, placement]);

  const buttonsConfig = useMemo(() => [{
    text: i18n.getString(_TL_('Apply')),
    icon: Checkmark20Regular,
    isApplyButton: true,
    onClick: handleSave,
  }, {
    text: i18n.getString(_TL_('Cancel')),
    icon: Dismiss20Regular,
    isCancelButton: true,
    onClick: handleCancel,
  }, {
    text: i18n.getString(_TL_('Reset')),
    icon: ArrowClockwise20Regular,
    onClick: handleReset,
  }], [handleSave, handleCancel, handleReset]);

  const placementEditView = usePlacementEditStyles();

  return (
    <div className={mergeClasses('placement-edit', placementEditView)}>
      <Card className="card">
        <CardHeader
          header={
            <div className="card-header">
              <Button
                appearance="subtle"
                icon={<ArrowLeft16Regular />}
                onClick={handleCancel}
                className="back-button"
              >
                <div className="ratio-text">{i18n.getString(_TL_('{{ratio}} {{previewType}}'), { ratio: aspectRatioUIString, previewType: i18n.getString(previewTypeString) })}</div>
              </Button>
              {renderIncludeAndExcludeButton()}
            </div>
          }
        />
        <div className="ad-preview-wrapper">
          <div className="placement-preview-text-wrapper">
            {i18n.getString(_TL_('Crop your image'))}
          </div>
          <div className="image-preview-image-wrapper">
            {
              imageCropView ||
              <Image
                className="image-item"
                // src={url}
                alt="image preview"
              />
            }
          </div>
        </div>
      </Card>
      <ImagePreviewView
        imageView={placementPreviewView}
        buttonsConfig={buttonsConfig}
        permissions={permissions}
      />
    </div>
  );
};

PlacementEditViewV2.propTypes = {
  placement: PropTypes.shape({}),
  aspectRatioUIString: PropTypes.string.isRequired,
  previewTypeString: PropTypes.string.isRequired,
  adType: PropTypes.string.isRequired,
  maxCropBox: PropTypes.shape({}),
  previewImage: PropTypes.shape({
    Type: PropTypes.string,
    Src: PropTypes.string,
    Width: PropTypes.number,
    Height: PropTypes.number,
  }).isRequired,
  previewTexts: PropTypes.shape({}).isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
  }).isRequired,
  goToPlacementListView: PropTypes.func.isRequired,
  updateCropSetting: PropTypes.func,
  onToggleAspectRatio: PropTypes.func,
  canExcludeAspectRatio: PropTypes.func,
  includedTypeList: PropTypes.arrayOf(PropTypes.string),
};

PlacementEditViewV2.defaultProps = {
  placement: null,
  maxCropBox: null,
  updateCropSetting: () => {},
  onToggleAspectRatio: null,
  canExcludeAspectRatio: null,
  includedTypeList: [],
};
